/**
 * L'écran de correction d'une copie (docs/12 §4).
 *
 * **Cet écran ne rejoue aucune règle de barème.** Le maximum d'une question, le
 * refus d'une note hors barème, l'obligation de noter chaque question ouverte :
 * tout cela est vérifié côté serveur, qui refuse avec le motif et la liste des
 * questions manquantes. L'écran borne le champ (`max` sur l'entrée) parce que
 * c'est une aide à la saisie, et rend le refus lisible parce que c'est son
 * travail — mais il n'a pas le dernier mot, et il ne fait pas semblant de
 * l'avoir : le bouton d'envoi reste **actif** même quand il manque une note.
 * Un bouton grisé sans explication laisse chercher pourquoi ; un refus nommé
 * dit quoi faire.
 *
 * **Le corrigé n'est pas là**, et ce n'est pas un oubli d'affichage : le serveur
 * ne le sert pas sur ce chemin. L'enseignant y aurait droit — c'est son cours —
 * mais il n'en a pas besoin pour lire une copie et la noter, et l'éditeur
 * d'exercice reste l'endroit où on le consulte.
 */
import { useParams } from "@tanstack/react-router";
import { useState, type SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  currentGrading,
  useGradeAttempt,
  useRevokeGrade,
  useSubmission,
  type GradingRecord,
  type MarkInput,
  type Submission,
  type SubmittedAnswer,
} from "@/features/learning/api/useGrading";
import { asDocument, toStored, type ContentDocument } from "@/shared/richtext/document";
import { RichContent } from "@/shared/richtext/RichContent";
import { RichTextEditor } from "@/shared/richtext/RichTextEditor";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Field } from "@/shared/ui/Field";
import { Skeleton } from "@/shared/ui/Skeleton";

export function GradeSubmissionPage() {
  // `/teach/teach/…` et non `/teach/…` : la route de layout est **pathless**
  // (`id: "teach"`), et son identifiant préfixe celui de ses enfants sans
  // ajouter de segment d'URL. L'URL reste `/teach/grading/{id}` ; c'est
  // l'identifiant de route qui double, et `tsc` est ce qui le dit.
  const { attemptId } = useParams({ from: "/teach/teach/grading/$attemptId" });
  const { t } = useTranslation("learning");
  const submission = useSubmission(attemptId);

  if (submission.isPending) {
    return <Skeleton className="mx-auto my-8 h-96 w-full max-w-3xl" label={t("grading.title")} />;
  }
  if (submission.isError) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <ErrorState error={submission.error} />
      </div>
    );
  }

  return <SubmissionView submission={submission.data} />;
}

function SubmissionView({ submission }: { submission: Submission }) {
  const { t } = useTranslation("learning");
  const grade = useGradeAttempt();
  const revoke = useRevokeGrade();
  const [marks, setMarks] = useState<Record<string, string>>({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<ContentDocument | null>(null);

  const graded = submission.status === "graded";
  const current = currentGrading(submission);

  function submit(event: SyntheticEvent) {
    event.preventDefault();
    // Seules les questions que l'enseignant a **touchées** partent. Envoyer une
    // note pour chaque question écraserait le verdict automatique de celles
    // qu'il n'a pas voulu reprendre — et `is_correct` repartirait à `null`
    // partout, sur des lignes que personne n'a relues.
    const body: MarkInput[] = Object.entries(marks)
      .filter(([, value]) => value.trim() !== "")
      .map(([questionId, value]) => ({
        question_id: questionId,
        points: value,
        feedback: comments[questionId]?.trim() === "" ? null : (comments[questionId] ?? null),
      }));
    grade.mutate({
      attemptId: submission.attempt_id,
      marks: body,
      feedback: toStored(feedback),
    });
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{submission.exercise_title}</h1>
        <p className="text-slate-600 dark:text-slate-400">
          {t("grading.by", {
            student: submission.student_name,
            course: submission.course_title,
            number: submission.attempt_number,
          })}
        </p>
      </header>

      {submission.instructions === null ? null : (
        <Card title={t("grading.instructions")}>
          <RichContent document={asDocument(submission.instructions)} />
        </Card>
      )}

      {graded ? (
        <Alert tone={submission.passed === true ? "success" : "error"}>
          <p className="font-medium">
            {t("grading.gradedScore", {
              score: submission.score,
              max: submission.max_score,
              percent: submission.score_pct,
            })}
          </p>
          {submission.feedback === null ? null : (
            <div className="mt-1 text-sm">
              <RichContent document={asDocument(submission.feedback)} />
            </div>
          )}
        </Alert>
      ) : null}

      <form onSubmit={submit} className="space-y-4">
        {submission.answers.map((answer) => (
          <AnswerCard
            key={answer.question_id}
            answer={answer}
            disabled={graded}
            mark={marks[answer.question_id] ?? ""}
            comment={comments[answer.question_id] ?? ""}
            onMark={(value) => {
              setMarks((previous) => ({ ...previous, [answer.question_id]: value }));
            }}
            onComment={(value) => {
              setComments((previous) => ({ ...previous, [answer.question_id]: value }));
            }}
          />
        ))}

        {graded ? null : (
          <Card title={t("grading.overallFeedback")}>
            <RichTextEditor
              label={t("grading.overallFeedback")}
              hideLabel
              content={feedback}
              onChange={setFeedback}
              minHeight="min-h-24"
            />
          </Card>
        )}

        {grade.isError ? <ErrorState error={grade.error} /> : null}
        {revoke.isError ? <ErrorState error={revoke.error} /> : null}

        <div className="flex flex-wrap gap-3">
          {graded ? (
            <Button
              type="button"
              variant="danger"
              disabled={revoke.isPending}
              onClick={() => {
                // Une annulation retire à un élève une note qu'il a lue, et peut
                // refermer la leçon qu'elle avait ouverte. La confirmation n'est
                // pas une politesse.
                if (window.confirm(t("grading.confirmRevoke"))) {
                  revoke.mutate(submission.attempt_id);
                }
              }}
            >
              {t("grading.revoke")}
            </Button>
          ) : (
            <Button type="submit" disabled={grade.isPending}>
              {t("grading.submit")}
            </Button>
          )}
        </div>
      </form>

      {submission.gradings.length === 0 ? null : (
        <Card title={t("grading.history")}>
          <ul className="space-y-2">
            {submission.gradings.map((record) => (
              <li key={record.id}>
                <HistoryRow record={record} isCurrent={record.id === current?.id} />
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

interface AnswerCardProps {
  answer: SubmittedAnswer;
  disabled: boolean;
  mark: string;
  comment: string;
  onMark: (value: string) => void;
  onComment: (value: string) => void;
}

/**
 * Une réponse d'élève, et de quoi la noter.
 *
 * Les propositions cochées sont rendues par **libellé** : `{"choice_ids": […]}`
 * ne se lit pas, et un correcteur devant des identifiants nus ne peut pas
 * corriger. Aucune d'elles ne dit si elle était juste — le schéma n'a pas ce
 * champ, ici comme du côté de l'élève.
 */
function AnswerCard({ answer, disabled, mark, comment, onMark, onComment }: AnswerCardProps) {
  const { t } = useTranslation("learning");
  return (
    <Card
      title={
        <span className="flex gap-1">
          {answer.position}. <RichContent document={asDocument(answer.prompt)} />
        </span>
      }
    >
      <div className="space-y-3">
        <div className="rounded-md bg-slate-50 p-3 text-sm dark:bg-slate-800">
          <p className="mb-1 text-xs text-slate-500 dark:text-slate-400">
            {t("grading.studentAnswer")}
          </p>
          <AnswerValue answer={answer} />
        </div>

        <p className="text-xs text-slate-500 dark:text-slate-400">
          {answer.is_auto_graded ? t("grading.autoGraded") : t("grading.manualQuestion")}
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label={t("grading.points", { max: answer.points })}
            type="number"
            step="0.01"
            min={0}
            max={Number(answer.points)}
            disabled={disabled}
            value={mark}
            placeholder={answer.points_awarded ?? ""}
            onChange={(event) => {
              onMark(event.target.value);
            }}
          />
          <Field
            label={t("grading.answerFeedback")}
            disabled={disabled}
            value={comment}
            onChange={(event) => {
              onComment(event.target.value);
            }}
          />
        </div>
      </div>
    </Card>
  );
}

/**
 * Ce que l'élève a rendu, quelle que soit la forme de sa réponse.
 *
 * La valeur est du JSON de forme libre (docs/05 §6) : son contenu suit le type
 * de la question, et le serveur ne le typera pas plus finement — `file_upload`
 * arrive en phase 8 sans migration. On lit donc les deux formes connues et l'on
 * rend une absence lisible pour le reste, plutôt que d'afficher `[object Object]`.
 */
function AnswerValue({ answer }: { answer: SubmittedAnswer }) {
  const { t } = useTranslation("learning");
  const chosen = answer.value["choice_ids"];
  if (Array.isArray(chosen)) {
    const labels = answer.choices
      .filter((choice) => chosen.includes(choice.id))
      .map((choice) => choice.label);
    return labels.length === 0 ? (
      <p className="italic">{t("grading.blank")}</p>
    ) : (
      <p>{labels.join(", ")}</p>
    );
  }
  const text = answer.value["text"];
  if (typeof text === "string" && text.trim() !== "") {
    return <p className="whitespace-pre-line">{text}</p>;
  }
  return <p className="italic">{t("grading.blank")}</p>;
}

/**
 * Une notation de l'historique — **y compris annulée**.
 *
 * Les annulées sont barrées et non retirées : les cacher ferait disparaître de
 * l'écran ce que la table existe pour conserver, et l'élève qui demande
 * « pourquoi ma note a changé ? » n'y trouverait rien à répondre.
 */
function HistoryRow({ record, isCurrent }: { record: GradingRecord; isCurrent: boolean }) {
  const { t } = useTranslation("learning");
  return (
    <div
      className={
        record.revoked_at === null
          ? "text-sm"
          : // `slate-500` et non `slate-400` sur fond clair : ce dernier ne
            // donne que 2,6:1, sous le 4,5:1 exigé par WCAG AA. Une notation
            // annulée est barrée, pas illisible — c'est le `line-through` qui
            // porte le sens, la couleur ne fait que l'appuyer.
            "text-sm text-slate-500 line-through dark:text-slate-400"
      }
    >
      <span className="font-medium">
        {t("attempt.percent", { value: record.score_pct })}
        {isCurrent ? ` — ${t("grading.currentGrade")}` : ""}
      </span>
      <span className="text-slate-500 dark:text-slate-400">
        {" — "}
        {record.graded_by_name ?? t("grading.unknownGrader")}
      </span>
      {record.feedback === null ? null : (
        <div className="text-slate-600 dark:text-slate-400">
          <RichContent document={asDocument(record.feedback)} />
        </div>
      )}
    </div>
  );
}
